/**
 * src/pages/reports/_chartPng.js — "Download PNG" for a dashboard panel.
 * Card P220 Part B1.
 *
 * ⚠ THE NAMING AND THE CAPTION ARE PURE FUNCTIONS, deliberately. Rasterising
 * needs a real canvas and cannot be tested in jsdom; choosing what the file
 * is called and what is printed on it can, and those are the parts that go
 * wrong — a deck full of chart.png, chart(1).png tells Carlos nothing three
 * weeks later.
 */

import { captureSvgPng } from './_chartCapture';
import { saveBlob } from './_download';

/** "Collected revenue" → "collected-revenue" */
export function slug(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    /* ⚠ Em dashes and the like become a single separator rather than
       vanishing, so "Collected — 12 months" does not become one long word. */
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'chart';
}

/**
 * The filename. ⛔ It carries the PANEL and the PERIOD — the card's
 * requirement, and the only two facts that make one export distinguishable
 * from the next in a downloads folder.
 */
export function chartPngFilename({ panel, period, pipeline } = {}) {
  const parts = [slug(panel)];
  if (period) parts.push(slug(period));
  /* ⚠ The pipeline only appears when it narrows something: "all-pipelines"
     in every filename is noise that hides the part that varies. */
  if (pipeline) parts.push(slug(pipeline));
  return `${parts.join('_')}.png`;
}

/**
 * What is burnt into the image. ⚠ Period and pipeline go in the SUBTITLE
 * rather than the title so the takeaway reads first, which is the one-message
 * rule the card asks for.
 */
export function chartPngCaption({ title, period, pipeline } = {}) {
  const bits = [period, pipeline].filter(Boolean);
  return { title: String(title || 'Chart'), subtitle: bits.join(' · ') || null };
}

/**
 * Capture a panel's chart and save it.
 *
 * ⛔ RETURNS false RATHER THAN THROWING when there is nothing to capture.
 * recharts renders no <svg> until it has a measured box, so a panel that is
 * collapsed, still loading, or showing its table instead has no chart — and
 * a failed download must not take the dashboard down with it.
 */
export async function downloadChartPng(node, meta = {}) {
  try {
    const bytes = await captureSvgPng(node, { caption: chartPngCaption(meta) });
    if (!bytes) return false;
    saveBlob(new window.Blob([bytes], { type: 'image/png' }), chartPngFilename(meta));
    return true;
  } catch (e) {
    return false;
  }
}
